import { ItemDetailsResponse } from '../types';
import styles from '../item-details.module.scss';

const SPECIALS = ['FedEx', 'USPS'];

const formatDistribution = (serviceLevel: string | null | undefined): string | null => {
  if (!serviceLevel) return null;
  return serviceLevel
    .split('_')
    .map(part => {
      const match = SPECIALS.find(s => s.toLowerCase() === part.toLowerCase());
      return match ?? (part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
    })
    .join(' ');
};

const parseSize = (size: string | null | undefined): string => {
  if (!size) return '–';
  const parts = size.toLowerCase().split('x');
  if (parts.length === 2) return `${parts[0]}" x ${parts[1]}"`;
  return size;
};

const capitalize = (s: string | null | undefined): string => {
  if (!s) return '–';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

interface DetailRowProps {
  label: string;
  value?: string | boolean | null;
  emptyText?: string;
  noBorder?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, emptyText = '–', noBorder }) => {
  let display: string;
  if (value == null) {
    display = emptyText;
  } else if (typeof value === 'boolean') {
    display = value ? 'Yes' : 'No';
  } else {
    display = value || emptyText;
  }

  const isEmpty = display === emptyText && emptyText === '–';

  return (
    <div className={`${styles.detailRow} ${noBorder ? styles.detailRowNoBorder : ''}`}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={isEmpty ? styles.detailValueEmpty : styles.detailValue}>{display}</span>
    </div>
  );
};

interface Props {
  data: ItemDetailsResponse | null;
}

const ItemDetailsTab: React.FC<Props> = ({ data }) => {
  if (!data) return null;

  const { itemDetails, overview } = data;

  const flags = itemDetails.flags?.length > 0 ? itemDetails.flags.join(', ') : null;
  const addOns = itemDetails.addOnProducts?.length > 0 ? itemDetails.addOnProducts.join(', ') : null;

  return (
    <div className={styles.tabContent}>
      <div className={styles.detailSection}>
        <DetailRow label="Account" value={itemDetails.account?.name} />
        <DetailRow label="Due Date" value={itemDetails.dueDate} />
        <DetailRow label="Order Total" value={overview.orderTotal ? ('$ ' + overview.orderTotal) : '-'} />
      </div>

      <div className={styles.detailSection}>
        <DetailRow label="Size" value={parseSize(itemDetails.size)} noBorder />
        <DetailRow label="Card Format" value={capitalize(itemDetails.cardFormat)} noBorder />
        <DetailRow label="Service Level" value={formatDistribution(itemDetails.serviceLevel) ?? overview.deliveryMethod} />
      </div>

      <div className={styles.detailSection}>
        <DetailRow label="Paper" value={itemDetails.paper} noBorder />
        <DetailRow label="Coating" value={itemDetails.coating} noBorder />
        <DetailRow label="Die Cut" value={itemDetails.dieCut} />
      </div>

      <div className={styles.detailSection}>
        <DetailRow label="Flags" value={flags} emptyText="None" />
        <DetailRow label="Add-On Products" value={addOns} emptyText="None" />
        <DetailRow label="Special Instructions" value={itemDetails.specialInstructions} emptyText="None" noBorder />
      </div>
    </div>
  );
};

export default ItemDetailsTab;
